import React, { forwardRef, memo } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { NumberInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-number-inline-edit-full/src/ui/number/index.tsx';
import type { NumberCell$key } from '@atlassian/jira-relay/src/__generated__/NumberCell.graphql';
import type { NumberCell_NumberInlineEditCell$key } from '@atlassian/jira-relay/src/__generated__/NumberCell_NumberInlineEditCell.graphql';
import { KNOWN_COLUMN_TYPES } from '@atlassian/jira-issue-table-common/src/constants.tsx';
import { StoryPointEstimateCell } from '../story-point-estimate/StoryPointEstimateCell.tsx';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import {
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { DUPLICATE_FIELD_EDIT_WIDTH } from '../../../constants.tsx';

export type Props = {
	fieldRef: NumberCell$key;
	isReadOnlyWithoutHoverPopover?: boolean;
	isFirstField?: boolean;
	hasDuplicates?: boolean;
};

export const NumberCell = memo(({ fieldRef, isFirstField, hasDuplicates }: Props) => {
	const data = useFragment<NumberCell$key>(
		graphql`
			fragment NumberCell on JiraNumberField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				type
				isStoryPointField
				...NumberCell_NumberInlineEditCell
					@arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
				...StoryPointEstimateCell @arguments(isInlineEditingEnabled: $isInlineEditingEnabled)
			}
		`,
		fieldRef,
	);

	const isSingleRowHeightEnabled = useIsSingleLineRowHeightEnabled();

	if (data.isStoryPointField || data.type === KNOWN_COLUMN_TYPES.STORY_POINT_ESTIMATE) {
		return (
			<StoryPointEstimateCell fieldRef={data} shouldRenderHoverPopover={isSingleRowHeightEnabled} />
		);
	}

	return (
		<NumberInlineEditCell
			fieldRef={data}
			isSingleRowHeightEnabled={isSingleRowHeightEnabled}
			isFirstField={isFirstField}
			hasDuplicates={hasDuplicates}
		/>
	);
});

type NumberInlineEditCellProps = {
	fieldRef: NumberCell_NumberInlineEditCell$key;
	isSingleRowHeightEnabled: boolean;
	isFirstField?: boolean;
	hasDuplicates?: boolean;
};

const NumberInlineEditCell = memo(
	({
		fieldRef,
		isSingleRowHeightEnabled,
		isFirstField,
		hasDuplicates,
	}: NumberInlineEditCellProps) => {
		const data = useFragment<NumberCell_NumberInlineEditCell$key>(
			graphql`
				fragment NumberCell_NumberInlineEditCell on JiraNumberField
				@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
					id
					lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
					...number_issueFieldNumberInlineEditFull_NumberInlineEditViewWithIsEditable_fragmentRef
				}
			`,
			fieldRef,
		);

		const isInlineEditingExtendedFieldSupportEnabled =
			useIsInlineEditingExtendedFieldSupportEnabled();
		// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
		// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
		const isEditable =
			'lazyIsEditableInIssueView' in data && isInlineEditingExtendedFieldSupportEnabled
				? data.lazyIsEditableInIssueView
				: false;
		useLazyLoadEditability(data.id, isEditable);

		if (hasDuplicates && isSingleRowHeightEnabled) {
			return (
				<NumberInlineEditViewWithIsEditable
					attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
					spacing="default"
					editViewPopup
					editViewPopupWidth={DUPLICATE_FIELD_EDIT_WIDTH}
					editViewPopupAlignBlock="start"
					// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
					editViewPopupTarget={isFirstField ? CustomTarget : undefined}
					fragmentRef={data}
					isEditable={isEditable ?? false}
					readViewFitContainerHeight
					isSingleRowHeightEnabled={isFirstField && isSingleRowHeightEnabled}
				/>
			);
		}

		return (
			<NumberInlineEditViewWithIsEditable
				attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
				spacing="default"
				editViewPopup
				editViewPopupAlignBlock="center"
				fragmentRef={data}
				isEditable={isEditable ?? false}
				readViewFitContainerHeight
				isSingleRowHeightEnabled={isSingleRowHeightEnabled}
			/>
		);
	},
);

const CustomTarget = forwardRef<HTMLDivElement, {}>((_, ref) => {
	return (
		<Box
			xcss={fg('nin_issue_table_rtl_fix') ? customTargetStyles : customTargetStylesOld}
			ref={ref}
		/>
	);
});

const customTargetStylesOld = xcss({
	width: 'space.0',
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	top: 'space.0',
});

const customTargetStyles = xcss({
	width: 'space.0',
	position: 'absolute',
	insetBlockStart: 'space.0',
});
